/**
 * 소개영상 오디오 도구 — 나레이션 다듬기, 배경음악 합성, 효과음
 *
 * 전부 브라우저(Web Audio) 안에서 돌고 외부 음원을 쓰지 않는다. 배경음악을 "합성" 하는 이유:
 * 앱에 음원 파일을 내장하면 저작권·라이선스 관리가 필요한데, 코드로 만든 소리는 그 문제가 없다.
 * 진짜 음악을 쓰고 싶으면 사용자가 자기 mp3 를 올리면 된다 (decodeAudioFile).
 *
 * 모든 함수는 mono Float32Array(-1..1) 를 주고받는다.
 */

/** 앞뒤 무음을 잘라낸다. TTS 출력은 앞뒤에 0.3~0.5초 정적이 붙어 와서 그대로 쓰면 늘어진다. */
export function trimSilence(samples, sampleRate, { threshold = 0.012, padSec = 0.06 } = {}) {
  if (!samples || samples.length < sampleRate * 0.2) return samples;
  const win = Math.round(sampleRate * 0.01); // 10ms 창의 RMS 로 판단
  const rms = (from) => {
    let s = 0;
    for (let i = from; i < from + win && i < samples.length; i++) s += samples[i] * samples[i];
    return Math.sqrt(s / win);
  };
  let start = 0;
  while (start + win < samples.length && rms(start) < threshold) start += win;
  let end = samples.length;
  while (end - win > start && rms(end - win) < threshold) end -= win;
  const pad = Math.round(sampleRate * padSec);
  start = Math.max(0, start - pad);
  end = Math.min(samples.length, end + pad);
  return samples.subarray(start, end);
}

/**
 * 음정을 유지한 채 속도만 바꾼다 (WSOLA). factor 1.2 = 20% 빠르게.
 * 단순 리샘플은 목소리가 얇아지므로(피치 상승) 쓰지 않는다. 음성에는 이 방식이 충분히 자연스럽다.
 */
export function timeStretch(samples, sampleRate, factor) {
  if (!samples || samples.length === 0 || Math.abs(factor - 1) < 0.01) return samples;
  const frame = Math.round(sampleRate * 0.025); // 25ms
  const hopOut = frame >> 1;
  const hopIn = hopOut * factor;
  const tol = Math.round(sampleRate * 0.008); // ±8ms 안에서 이어 붙일 자리를 찾는다
  const outLen = Math.floor(samples.length / factor);
  const out = new Float32Array(outLen + frame);
  const win = new Float32Array(frame);
  for (let i = 0; i < frame; i++) win[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (frame - 1));

  // 첫 프레임은 그대로
  for (let i = 0; i < frame && i < samples.length; i++) out[i] = samples[i] * win[i];
  let prevPos = 0; // 직전에 가져다 쓴 입력 위치
  for (let k = 1; k * hopOut + frame < out.length; k++) {
    const nominal = Math.round(k * hopIn);
    if (nominal + frame + tol >= samples.length) break;
    // 자연스러운 연속: 직전 프레임에서 hopOut 만큼 진행한 자리. 그것과 가장 닮은 곳을 nominal 근처에서 찾는다.
    const natural = prevPos + hopOut;
    let best = 0;
    let bestScore = -Infinity;
    const lo = Math.max(-tol, -nominal);
    for (let off = lo; off <= tol; off += 2) {
      let score = 0;
      const a = nominal + off;
      for (let i = 0; i < frame; i += 3) score += samples[a + i] * samples[natural + i];
      if (score > bestScore) { bestScore = score; best = off; }
    }
    const pos = nominal + best;
    const o = k * hopOut;
    for (let i = 0; i < frame; i++) out[o + i] += samples[pos + i] * win[i];
    prevPos = pos;
  }
  return out.subarray(0, outLen);
}

/** 사용자가 올린 음악 파일 → mono Float32 (원하는 샘플레이트로) */
export async function decodeAudioFile(file, sampleRate) {
  const buf = await file.arrayBuffer();
  const probe = new OfflineAudioContext(1, 1, sampleRate);
  const decoded = await probe.decodeAudioData(buf);
  // 리샘플 + mono 합치기를 OfflineAudioContext 에 맡긴다
  const length = Math.ceil(decoded.duration * sampleRate);
  const ctx = new OfflineAudioContext(1, length, sampleRate);
  const src = ctx.createBufferSource();
  src.buffer = decoded;
  src.connect(ctx.destination);
  src.start(0);
  const rendered = await ctx.startRendering();
  return rendered.getChannelData(0);
}

// ── 배경음악 합성 ────────────────────────────────────────────────

/** 톤별 음악 성격 */
const MOODS = {
  calm: { bpm: 72, key: 220.0, progression: [[0, 4, 7, 11], [5, 9, 12, 16], [-3, 0, 4, 7], [2, 5, 9, 12]], arp: 'slow', drums: false, cutoff: 1100 },
  energetic: { bpm: 112, key: 246.9, progression: [[0, 4, 7], [-5, -1, 2], [-3, 0, 4], [-7, -3, 0]], arp: 'fast', drums: true, cutoff: 2200 },
  premium: { bpm: 84, key: 196.0, progression: [[0, 3, 7, 10], [-4, 0, 3, 7], [-7, -4, 0, 3], [-2, 2, 5, 9]], arp: 'sparse', drums: false, cutoff: 900 },
};

const semi = (base, n) => base * Math.pow(2, n / 12);

/**
 * 영상 길이만큼 앰비언트 배경음악을 만든다. 패드(코드) + 베이스 + 가벼운 아르페지오(+경쾌 모드는 리듬).
 * "음악" 이라기보다 "깔림음" 을 목표로 한다 — 나레이션을 방해하지 않는 게 우선이다.
 */
export async function generateMusic({ durationSec, mood = 'calm', sampleRate }) {
  const m = MOODS[mood] || MOODS.calm;
  const length = Math.ceil(durationSec * sampleRate);
  const ctx = new OfflineAudioContext(1, length, sampleRate);
  const master = ctx.createGain();
  master.gain.value = 0.8;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 4000;
  master.connect(lp).connect(ctx.destination);

  const beat = 60 / m.bpm;
  const bar = beat * 4;
  const chordLen = bar * 2;

  // 패드: 코드마다 음 하나당 살짝 어긋난 삼각파 2개 + 느린 필터
  const padBus = ctx.createGain();
  padBus.gain.value = 0.16;
  const padLp = ctx.createBiquadFilter();
  padLp.type = 'lowpass';
  padLp.frequency.value = m.cutoff;
  padLp.Q.value = 0.7;
  padBus.connect(padLp).connect(master);
  for (let t = 0, ci = 0; t < durationSec; t += chordLen, ci++) {
    const chord = m.progression[ci % m.progression.length];
    const end = Math.min(durationSec, t + chordLen);
    for (const n of chord) {
      for (const detune of [-6, 6]) {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = semi(m.key, n);
        osc.detune.value = detune;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(1, t + 1.2); // 부드럽게 들어와서
        g.gain.setValueAtTime(1, Math.max(t + 1.2, end - 0.8));
        g.gain.linearRampToValueAtTime(0, end + 0.4); // 다음 코드와 살짝 겹치며 나간다
        osc.connect(g).connect(padBus);
        osc.start(t);
        osc.stop(end + 0.5);
      }
    }
    // 베이스: 루트 한 옥타브 아래 사인
    const bass = ctx.createOscillator();
    bass.type = 'sine';
    bass.frequency.value = semi(m.key, chord[0]) / 2;
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(0, t);
    bg.gain.linearRampToValueAtTime(0.14, t + 0.6);
    bg.gain.setValueAtTime(0.14, Math.max(t + 0.6, end - 0.5));
    bg.gain.linearRampToValueAtTime(0, end + 0.2);
    bass.connect(bg).connect(master);
    bass.start(t);
    bass.stop(end + 0.3);
  }

  // 아르페지오: 짧은 플럭 (사인 + 배음, 빠른 감쇠)
  const pluck = (time, freq, gain, decay) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const h = ctx.createOscillator();
    h.type = 'triangle';
    h.frequency.value = freq * 2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, time);
    g.gain.linearRampToValueAtTime(gain, time + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0005, time + decay);
    const hg = ctx.createGain();
    hg.gain.value = 0.25;
    osc.connect(g);
    h.connect(hg).connect(g);
    g.connect(master);
    osc.start(time); h.start(time);
    osc.stop(time + decay + 0.05); h.stop(time + decay + 0.05);
  };
  const arpStep = m.arp === 'fast' ? beat / 2 : m.arp === 'slow' ? beat : beat * 1.5;
  for (let t = bar; t < durationSec - 1; t += arpStep) {
    const ci = Math.floor(t / chordLen) % m.progression.length;
    const chord = m.progression[ci];
    const idx = Math.floor(t / arpStep) % chord.length;
    if (m.arp === 'sparse' && Math.floor(t / arpStep) % 3 === 1) continue; // 프리미엄은 듬성듬성
    pluck(t, semi(m.key, chord[idx]) * 2, m.arp === 'fast' ? 0.07 : 0.09, m.arp === 'fast' ? 0.5 : 1.4);
  }

  // 경쾌 모드: 아주 가벼운 리듬 (킥은 사인 드롭, 하이햇은 필터 노이즈)
  if (m.drums) {
    const noiseBuf = ctx.createBuffer(1, sampleRate * 0.08, sampleRate);
    const nd = noiseBuf.getChannelData(0);
    for (let i = 0; i < nd.length; i++) nd[i] = (Math.random() * 2 - 1) * Math.exp(-i / (sampleRate * 0.012));
    for (let t = bar; t < durationSec - 0.5; t += beat) {
      const kick = ctx.createOscillator();
      kick.frequency.setValueAtTime(140, t);
      kick.frequency.exponentialRampToValueAtTime(45, t + 0.12);
      const kg = ctx.createGain();
      kg.gain.setValueAtTime(0.18, t);
      kg.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      kick.connect(kg).connect(master);
      kick.start(t); kick.stop(t + 0.3);
      for (const off of [beat / 2]) {
        const hat = ctx.createBufferSource();
        hat.buffer = noiseBuf;
        const hp = ctx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 7000;
        const hg = ctx.createGain();
        hg.gain.value = 0.05;
        hat.connect(hp).connect(hg).connect(master);
        hat.start(t + off);
      }
    }
  }

  const rendered = await ctx.startRendering();
  const out = rendered.getChannelData(0);
  // 분위기마다 소리 크기가 달라지지 않게 RMS 0.15 로 맞춘다 (피크는 0.9 이하)
  let sum = 0, peak = 0;
  for (let i = 0; i < out.length; i++) { sum += out[i] * out[i]; if (Math.abs(out[i]) > peak) peak = Math.abs(out[i]); }
  const rms = Math.sqrt(sum / out.length) || 1;
  const scale = Math.min(0.15 / rms, peak ? 0.9 / peak : 1);
  for (let i = 0; i < out.length; i++) out[i] *= scale;
  // 처음 1초 페이드인, 마지막 2초 페이드아웃
  const fi = Math.min(out.length, sampleRate);
  for (let i = 0; i < fi; i++) out[i] *= i / fi;
  const fo = Math.min(out.length, sampleRate * 2);
  for (let i = 0; i < fo; i++) out[out.length - 1 - i] *= i / fo;
  return out;
}

// ── 효과음 ───────────────────────────────────────────────────────

/** 장면 전환용 우시(whoosh): 노이즈를 밴드패스로 쓸어올리는 0.45초 */
export async function synthWhoosh(sampleRate) {
  const dur = 0.45;
  const ctx = new OfflineAudioContext(1, Math.ceil(dur * sampleRate), sampleRate);
  const buf = ctx.createBuffer(1, Math.ceil(dur * sampleRate), sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.Q.value = 1.2;
  bp.frequency.setValueAtTime(300, 0);
  bp.frequency.exponentialRampToValueAtTime(2600, dur * 0.7);
  bp.frequency.exponentialRampToValueAtTime(900, dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, 0);
  g.gain.linearRampToValueAtTime(1, dur * 0.35);
  g.gain.exponentialRampToValueAtTime(0.001, dur);
  src.connect(bp).connect(g).connect(ctx.destination);
  src.start(0);
  const rendered = await ctx.startRendering();
  return rendered.getChannelData(0);
}

// ── 믹스 유틸 ────────────────────────────────────────────────────

/**
 * 나레이션이 있는 구간에서 음악을 낮추는 게인 곡선(덕킹). 0.25초 램프로 부드럽게.
 * @param {Array<{start:number,end:number}>} speech  나레이션 구간(초)
 */
export function buildDuckingCurve(length, sampleRate, speech, { duckTo = 0.35, rampSec = 0.25 } = {}) {
  const curve = new Float32Array(length).fill(1);
  const ramp = Math.round(sampleRate * rampSec);
  for (const { start, end } of speech) {
    const a = Math.max(0, Math.round(start * sampleRate) - ramp);
    const b = Math.min(length, Math.round(end * sampleRate) + ramp);
    for (let i = a; i < b; i++) {
      const inRamp = Math.min(1, (i - a) / ramp, (b - i) / ramp);
      const g = 1 - (1 - duckTo) * Math.max(0, Math.min(1, inRamp));
      if (g < curve[i]) curve[i] = g;
    }
  }
  return curve;
}

/** 넘치는 피크를 부드럽게 눌러준다 (하드 클리핑 대신) */
export function softClip(track) {
  for (let i = 0; i < track.length; i++) {
    const v = track[i];
    if (v > 0.9 || v < -0.9) track[i] = Math.tanh(v);
  }
  return track;
}
